import React, { useEffect, useState } from 'react';

import DataTableContainer from '../../components/DataTableContainer';
import MyDataTable from '../../components/MyDataTable';
import { ServerFarmInterface, StreamServer, streamServersService, TimelineProvider, VideoStorageProvider } from '../../services/central-api/stream-servers';
import { translate } from '../../services/translate';
import { View } from 'react-native';
import Badge from '../../components/Badge';
import DropdownButton from '../../components/DropdownButton';
import ConfirmModal from '../../components/ConfirmModal';
import DataTableHeader from '../../components/DataTableHeader';
import MyDropDownPicker from '../../components/MyDropDownPicker';
import moment from 'moment';
import PurgeCamerasModal from './PurgeCamerasModal';
import { RootNavigation } from '../../Routes';

interface ServerFarmDetailServersParams {
    serverFarm: ServerFarmInterface;
    setServerPoolId: React.Dispatch<React.SetStateAction<string | null>>;
    serverPoolId: string | null;
    setTimelineProviderId: React.Dispatch<React.SetStateAction<number | null>>;
    timelineProviderId: number | null;
    setVideoStorageProviderId: React.Dispatch<React.SetStateAction<number | null>>;
    videoStorageProviderId: number | null;
    refetch: () => void;
    rootNavigation: RootNavigation;
}

export default function ServerFarmDetailServers(params: ServerFarmDetailServersParams) {
    const [disableModalVisible, setDisableModalVisible] = useState(false);
    const [enableModalVisible, setEnableModalVisible] = useState(false);
    const [markAsDeletedModalVisible, setMarkAsDeletedModalVisible] = useState(false);
    const [purgeCamerasModalVisible, setPurgeCamerasModalVisible] = useState(false);
    const [selectedServer, setSelectedServer] = useState<StreamServer | null>(null);

    const [timelineProviders, setTimelineProviders] = useState<{ [timelineProviderId: number]: TimelineProvider | undefined; }>([]);
    const [videoStorageProviders, setVideoStorageProviders] = useState<{ [videoStorageProviderId: number]: VideoStorageProvider | undefined; }>([]);

    const [serverPoolDropdownOpen, setServerPoolDropdownOpen] = useState(false);
    const [videoStorageProviderDropdownOpen, setVideoStorageProviderDropdownOpen] = useState(false);
    const [timelineProviderDropdownOpen, setTimelineProviderDropdownOpen] = useState(false);

    const [availableServerPools, setAvailableServerPools] = useState<{ id: string; }[]>([]);

    useEffect(() => {
        if (!params.serverFarm) {
            return;
        }
        setTimelineProviders(params.serverFarm.timelineProviders.reduce((previousValue, currentValue) => {
            previousValue[currentValue.id] = currentValue;
            return previousValue;
        }, {} as { [timelineProviderId: number]: TimelineProvider; }));
        setVideoStorageProviders(params.serverFarm.videoStorageProviders.reduce((previousValue, currentValue) => {
            previousValue[currentValue.id] = currentValue;
            return previousValue;
        }, {} as { [videoStorageProviderId: number]: VideoStorageProvider; }));
    }, [params.serverFarm]);

    return (<View style={{ flex: 1 }}>
        <DataTableHeader
            title={translate('servers')}
            zIndex={1}
            filter={<View style={{
                flex: 1,
                flexDirection: 'row',
                gap: 5,
            }}>
                <View>
                    <MyDropDownPicker
                        placeholder={translate('serverPools')}
                        searchable={true}
                        items={[{}, ...params.serverFarm.serverPools.map(sp => ({
                            value: sp.id,
                            label: sp.id,
                        }))]}
                        open={serverPoolDropdownOpen}
                        setOpen={setServerPoolDropdownOpen}
                        setValue={params.setServerPoolId}
                        value={params.serverPoolId}
                        zIndex={3}
                    />
                </View>
                <View>
                    <MyDropDownPicker
                        placeholder={translate('timelineProviders')}
                        searchable={true}
                        items={[{}, ...params.serverFarm.timelineProviders.map(tp => ({
                            value: tp.id,
                            label: `${tp.id} - ${timelineProviders[tp.id]?.timelineType || ''} - ${moment(timelineProviders[tp.id]?.createdAt || '').format('DD/MM/YYYY')}`,
                        }))]}
                        open={timelineProviderDropdownOpen}
                        setOpen={setTimelineProviderDropdownOpen}
                        setValue={params.setTimelineProviderId}
                        value={params.timelineProviderId}
                        zIndex={2}
                    />
                </View>
                <View>
                    <MyDropDownPicker
                        placeholder={translate('videoStorageProviders')}
                        searchable={true}
                        items={[{}, ...params.serverFarm.videoStorageProviders.map(vsp => ({
                            value: vsp.id,
                            label: `${vsp.id} - ${videoStorageProviders[vsp.id]?.blobStorageType || ''} - ${moment(videoStorageProviders[vsp.id]?.createdAt || '').format('DD/MM/YYYY')}`,
                        }))]}
                        open={videoStorageProviderDropdownOpen}
                        setOpen={setVideoStorageProviderDropdownOpen}
                        setValue={params.setVideoStorageProviderId}
                        value={params.videoStorageProviderId}
                        zIndex={1}
                    />
                </View>
            </View>}
        />
        <DataTableContainer
            dataTable={<MyDataTable
                data={params.serverFarm.servers.filter((server) => {
                    if (params.serverPoolId && server.serverPoolId !== params.serverPoolId) {
                        return false;
                    }
                    if (params.timelineProviderId && server.timelineProviderId !== params.timelineProviderId) {
                        return false;
                    }
                    if (params.videoStorageProviderId && server.videoStorageProviderId !== params.videoStorageProviderId) {
                        return false;
                    }
                    return true;
                })}
                pointerOnHover
                onRowClicked={(row) => {
                    params.rootNavigation.navigate('CRM', {
                        screen: 'Management', params: {
                            page: 0,
                            limit: 25,
                            serverId: row.id,
                        }
                    });
                }}
                columns={[{
                    name: translate('id'),
                    cell: row => row.id,
                    minWidth: '75px',
                    grow: 0,
                }, {
                    name: translate('domain'),
                    selector: row => row.domain,
                }, {
                    name: translate('serverPool'),
                    selector: row => row.serverPoolId,
                }, {
                    name: translate('createdAt'),
                    cell: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
                    minWidth: '150px',
                    grow: 0,
                }, {
                    name: translate('status'),
                    cell: row => {
                        return <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                            {row.state === 'enabled' && <Badge mode='SQUARE' color='green'>{row.state}</Badge>}
                            {row.state === 'manual_disabled' && <Badge mode='SQUARE' color={row.serverPoolToPurgeCamerasId && row.cameraCount > 0 ? 'purple' : 'orange'}>{row.serverPoolToPurgeCamerasId && row.cameraCount > 0 ? 'purging' : row.state}</Badge>}
                            {row.state === 'waiting_for_deletion' && <Badge mode='SQUARE' color='red'>{row.state}</Badge>}
                            {row.state === 'waiting_server_config' && <Badge mode='SQUARE' color='yellow'>{row.state}</Badge>}
                        </View>;
                    },
                    minWidth: '170px',
                    grow: 0,
                }, {
                    name: translate('cameras'),
                    cell: row => row.cameraCount,
                    minWidth: '100px',
                    grow: 0,
                    center: true,
                }, {
                    name: translate('timelineProvider'),
                    selector: row => {
                        return `${row.timelineProviderId} - ${timelineProviders[row.timelineProviderId]?.timelineType || ''} - ${moment(timelineProviders[row.timelineProviderId]?.createdAt || '').format('DD/MM/YYYY')}`;
                    },
                }, {
                    name: translate('videoStorageProvider'),
                    selector: row => {
                        return `${row.videoStorageProviderId} - ${videoStorageProviders[row.videoStorageProviderId]?.blobStorageType || ''} - ${moment(videoStorageProviders[row.videoStorageProviderId]?.createdAt || '').format('DD/MM/YYYY')}`;
                    },
                }, {
                    name: '',
                    button: true,
                    wrap: true,
                    cell: (row) => {
                        return (
                            <DropdownButton
                                size={320}
                                offsetX={-168}
                                actions={[{
                                    content: translate('changeTimelineProvider'),
                                    omit: !['enabled', 'manual_disabled'].includes(row.state),
                                    onPress: () => {
                                        // setSelectedServer(row);
                                    },
                                }, {
                                    content: translate('changeVideoStorageProvider'),
                                    omit: !['enabled', 'manual_disabled'].includes(row.state),
                                    onPress: () => {
                                        // setSelectedServer(row);
                                    },
                                }, {
                                    omit: row.state !== 'manual_disabled' || row.serverPoolToPurgeCamerasId !== null,
                                    content: translate('purgeCameras'),
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setPurgeCamerasModalVisible(true);

                                        const serverServerPool = params.serverFarm.serverPools.find(sp => sp.id === row.serverPoolId);

                                        if (!serverServerPool) {
                                            return setAvailableServerPools([]);
                                        }

                                        const serverPools = params.serverFarm.serverPools.filter(sp => sp.type === serverServerPool.type);

                                        setAvailableServerPools(serverPools);
                                    },
                                }, {
                                    omit: row.state !== 'enabled',
                                    content: translate('disable'),
                                    variant: 'warning',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setDisableModalVisible(true);
                                    },
                                }, {
                                    omit: row.state !== 'manual_disabled',
                                    content: translate('enable'),
                                    variant: 'success',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setEnableModalVisible(true);
                                    },
                                }, {
                                    omit: (row.state !== 'manual_disabled' && row.cameraCount !== 0) || row.state === 'waiting_for_deletion',
                                    content: translate('markAsDeleted'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setMarkAsDeletedModalVisible(true);
                                    },
                                }]}
                            />
                        );
                    }
                }]}
            />}
        />
        <ConfirmModal
            maxWidth={400}
            setModalVisible={setEnableModalVisible}
            isModalVisible={enableModalVisible}
            customMessage={translate('enableServer', {
                server: `${selectedServer?.id} - ${selectedServer?.domain}`,
            })}
            customConfirm={translate('confirm')}
            onSubmit={async () => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    await streamServersService.enableServer(selectedServer.id);
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
            onCancel={() => {
                setSelectedServer(null);
            }}

        />
        <ConfirmModal
            maxWidth={400}
            setModalVisible={setDisableModalVisible}
            isModalVisible={disableModalVisible}
            customMessage={translate('disableServer', {
                server: `${selectedServer?.id} - ${selectedServer?.domain}`,
            })}
            customConfirm={translate('confirm')}
            onSubmit={async () => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    await streamServersService.disableServer(selectedServer.id);
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
            onCancel={() => {
                setSelectedServer(null);
            }}

        />
        <ConfirmModal
            maxWidth={400}
            setModalVisible={setMarkAsDeletedModalVisible}
            isModalVisible={markAsDeletedModalVisible}
            customMessage={translate('markServerAsDeleted', {
                server: `${selectedServer?.id} - ${selectedServer?.domain}`,
            })}
            customConfirm={translate('confirm')}
            onSubmit={async () => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    await streamServersService.markServerToBeDeleted(selectedServer.id);
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
            onCancel={() => {
                setSelectedServer(null);
            }}

        />
        <PurgeCamerasModal
            maxWidth={400}
            setModalVisible={setPurgeCamerasModalVisible}
            isModalVisible={purgeCamerasModalVisible}
            availableServerPools={availableServerPools}
            onSubmit={async (serverPoolId) => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    await streamServersService.purgeServerCamerasToServerPool(selectedServer.id, serverPoolId);
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
            onCancel={() => {
                setSelectedServer(null);
            }}
        />
    </View>);
}
